import { Capacitor } from "@capacitor/core";
import type {
  Book,
  BookRecord,
  BookSearchResult,
  ChapterDetails,
  GetChaptersOptions,
  GetJobsOptions,
  PaginatedChapters,
  PaginatedJobs,
  SearchResponse,
  SourceInfo,
  SourceListItem,
  Task,
} from "#/types";

const isWeb = Capacitor.getPlatform() === "web";
const isDev = process.env.NODE_ENV !== "production";

let API_URL: string;
if (isWeb && isDev) {
  API_URL = "http://localhost:8080/api";
} else if (isWeb && !isDev) {
  API_URL = "/api";
} else if (!isWeb && isDev) {
  API_URL = "http://rlinux:8080/api";
} else {
  API_URL = "https://wn.upadhyayji.in/api";
}

console.log("API config:", {
  DEV: import.meta.env.DEV,
  platform: Capacitor.getPlatform(),
  API_BASE: API_URL,
});

const AUTH_TOKEN_KEY = "auth_token";

export function getAuthToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function setAuthToken(token: string | null): void {
  if (token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  }
}

export async function addToLibrary(url: string, sourceId?: string): Promise<Book> {
  return post<Book>("/book", { source_id: sourceId, url });
}

export async function cancelJob(id: string | number): Promise<void> {
  await post(`/download-job/${id}/cancel`);
}

export async function checkUpdates(id: string | number): Promise<BookRecord> {
  return get<BookRecord>(`/book/${id}/check`);
}

export async function deleteBook(id: string | number): Promise<void> {
  await del(`/book/${id}`);
}

export async function deleteChapters(bookId: string | number, chapterId: number): Promise<void> {
  await post(`/book/${bookId}/chapters/${chapterId}`);
}

export async function exportSource(id: string): Promise<SourceInfo> {
  return get<SourceInfo>(`/sources/${id}/export`);
}

export async function getBook(id: string | number): Promise<Book> {
  return get<Book>(`/book/${id}`);
}

export async function getChapters(
  id: string | number,
  options?: GetChaptersOptions,
): Promise<PaginatedChapters> {
  const params = new URLSearchParams();
  if (options?.cursor) {
    params.append("cursor", options.cursor.toString());
  }
  if (options?.limit) {
    params.append("limit", options.limit.toString());
  }
  if (options?.desc !== undefined) {
    params.append("desc", options.desc.toString());
  }
  return get<PaginatedChapters>(`/book/${id}/chapters?${params.toString()}`);
}

export async function getChapterDetails(
  bookId: string | number,
  chapterId: string | number,
): Promise<ChapterDetails> {
  return get<ChapterDetails>(`/book/${bookId}/chapters/${chapterId}`);
}

export function apiUrl(path: string): string {
  return `${API_URL}${path}`;
}

export function getCoverUrl(bookId: number): string {
  return apiUrl(`/book/${bookId}/cover`);
}

export function getEpubDownloadUrl(bookId: number, startChapterId: number, count: number): string {
  const token = getAuthToken();
  const params = new URLSearchParams({
    start_chapter_id: startChapterId.toString(),
    count: count.toString(),
  });
  if (token) {
    params.append("auth-token", token);
  }

  return apiUrl(`/book/${bookId}/epub?${params.toString()}`);
}

export function getExportSourceUrl(sourceId: string): string {
  return apiUrl(`/sources/${sourceId}/export`);
}

export async function getJobs(options?: GetJobsOptions): Promise<PaginatedJobs> {
  const params = new URLSearchParams();
  if (options?.cursor) {
    params.append("cursor", options.cursor.toString());
  }
  if (options?.limit) {
    params.append("limit", options.limit.toString());
  }
  if (options?.book_id) {
    params.append("book_id", options.book_id.toString());
  }
  if (options?.source) {
    params.append("source", options.source);
  }
  const queryString = params.toString();
  return get<PaginatedJobs>(`/download-job${queryString ? `?${queryString}` : ""}`);
}

export async function getLibrary(): Promise<Book[]> {
  return get<Book[]>("/book");
}

export async function getSources(): Promise<SourceInfo[]> {
  return get<SourceInfo[]>("/sources");
}

export async function toggleSourceDisabled(sourceId: string): Promise<void> {
  await put(`/sources/${sourceId}/disable`);
}

export async function importSource(configJson: string): Promise<void> {
  await post("/sources", { config_json: configJson });
}

export async function deleteSource(id: string): Promise<void> {
  await del(`/sources/${id}`);
}

export async function search(query: string, source: string): Promise<SearchResponse> {
  return get<SearchResponse>(
    `/sources/search-books?q=${encodeURIComponent(query)}&source=${source}`,
  );
}

export async function searchBooks(query: string, limit: number = 20): Promise<BookSearchResult[]> {
  return get<BookSearchResult[]>(`/book/search?q=${encodeURIComponent(query)}&limit=${limit}`);
}

export async function updateProgress(bookId: number | string, chapterId: number): Promise<void> {
  await post(`/progress/${bookId}`, { chapter_id: chapterId });
}

export async function startDownload(
  id: string | number,
  useExtension: boolean,
  limit?: number,
): Promise<void> {
  await post(`/book/${id}/download`, {
    use_extension: useExtension,
    limit,
  });
}

//////////////// Generic helper methods //////////////

interface RequestOptions extends RequestInit {
  json?: any;
}

export class ApiError extends Error {
  constructor(
    public override message: string,
    public response: Response,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request(path: string, options: RequestOptions = {}) {
  const headers = new Headers(options.headers);
  if (options.json) {
    headers.set("Content-Type", "application/json");
    options.body = JSON.stringify(options.json);
  }
  // Add Authorization header if token exists
  const token = getAuthToken();
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  const response = await fetch(apiUrl(path), { ...options, headers });
  if (!response.ok) {
    let errMessage = "Request failed";
    try {
      const body = await response.json();
      errMessage = body.message || body.error || errMessage;
    } catch {
      try {
        errMessage = (await response.text()) || errMessage;
      } catch {}
    }
    throw new ApiError(errMessage, response);
  }
  return response;
}

async function requestJson<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const res = await request(path, options);
  const text = await res.text();
  return text ? JSON.parse(text) : ({} as T);
}

export const get = <T>(url: string, options?: RequestOptions) =>
  requestJson<T>(url, { ...options, method: "GET" });

export const post = <T>(url: string, body?: any, options?: RequestOptions) =>
  requestJson<T>(url, { ...options, json: body, method: "POST" });

export const put = <T>(url: string, body?: any, options?: RequestOptions) =>
  requestJson<T>(url, { ...options, json: body, method: "PUT" });

export const del = <T>(url: string, options?: RequestOptions) =>
  requestJson<T>(url, { ...options, method: "DELETE" });
